import React, { useState, useEffect } from 'react';
import { GoogleMap, Marker, MarkerClusterer, InfoBox } from '@react-google-maps/api';
import StoreList from './StoreList';
import '../App.css';

const MapClass = ({ resetLocationClick, getCurrentLocation, setClusterClick, ...props }) => {

    const [storeData, setStoreData] = useState(null);
    const [currentStoreData, setCurrentStoreData] = useState(null);
    const [clientLatitude, setClientLatitude] = useState(0);
    const [clientLongitude, setClientLongitude] = useState(0);
    const [currentZoom, setCurrentZoom] = useState(10);
    const [mapDetails, setMapDetails] = useState(null);
    const [clusterDetails, setClusterDetails] = useState(null);
    const [locationClick, setLocationClick] = useState(false);
    const [mapCenter, setMapCenter] = useState(null);
    const [infoBoxPosition, setInfoBoxPosition] = useState({ lat: null, lng: null, name: null, address1: null, address2: null, hours: null })

    useEffect(() => {  // on app load  
        fetchData();  // get list of store data
        setClientLatitude(props.clientLatitude);
        setClientLongitude(props.clientLongitude);
        setCurrentZoom(props.zoom);
        setMapCenter(props.mapCenter);
    }, []);

    const fetchData = async () => {  // get list of stores from the website
        try {
            const response = await fetch("/wp-json/gfs/v1/location/locations");
            const jsonData = await response.json();
            setStoreData(jsonData);
            setCurrentStoreData(jsonData);
        } catch (error) {
            console.error('Error fetching data:', error);
        };
    };

    const locationClickEvent = (latitude, longitude) => {  //  fires if the user clicks on the Look Up button
        // move the center of the map to where the geocoding comes up with
        if (latitude && longitude) {
            setLocationClick("click");
            setMapCenter(latitude + "," + longitude);
            setClientLatitude(latitude);
            setClientLongitude(longitude);
            setCurrentZoom(13);
            getCurrentLocation(latitude, longitude);
        } else {
            setMapCenter(props.clientLatitude + "," + props.clientLongitude);
            setClientLatitude(props.clientLatitude);
            setClientLongitude(props.clientLongitude);
            setCurrentZoom(10);
            getCurrentLocation(props.clientLatitude, props.clientLongitude);
        };
    };

    const locationDragEvent = () => {  // fires if the user drags the map
        // this is not the cleanest way to find the center of the map, but it works better for the store list 
        const url = mapDetails.state.map.mapUrl;
        const ans = url.substring(url.indexOf('?ll=') + 4, url.indexOf('&'));
        const currentLatitude = parseFloat(ans.substring(0, ans.indexOf(',')));
        const currentLongitude = parseFloat(ans.substring(ans.indexOf(',') + 1, ans.length));
        const zoom = parseInt(url.substring(url.indexOf('z=') + 2, url.indexOf('&t=m')));

        setMapCenter(currentLatitude + "," + currentLongitude);
        setClientLatitude(currentLatitude);
        setClientLongitude(currentLongitude);
        setCurrentZoom(zoom);
        getCurrentLocation(currentLatitude, currentLongitude);
    };

    const mapCenterChanged = () => {  // set new map center, decide if it's a drag event or a new location click
        setCurrentStoreData(storeData);
        if (mapDetails) {
            if (mapDetails.state.map.mapUrl) {
                if (props.locationClick || locationClick) {
                    locationClickEvent();
                    resetLocationClick(false);

                } else if (props.locationClick == false) {
                    locationDragEvent();
                };
            };
        };
    };

    const convertTimeFormat = (timeString) => {
        const [hours, minutes] = timeString.split(":");
        let parsedHours = parseInt(hours);
        let period = "am";
      
        if (parsedHours >= 12) {
          period = "pm";
          if (parsedHours > 12) {
            parsedHours -= 12;
          };
        };
      
        return `${parsedHours}:${minutes}${period}`;
    };

    const getHoursToday = (store) => {
        const d = new Date();
        const day = d.getDay();
        const year = d.getFullYear();
        const month = (d.getMonth() + 1).toString().padStart(2, '0');
        const date = d.getDate().toString().padStart(2, '0');
        const dateOverride = year + "-" + month + "-" + date;
        const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

        if( store.open == "no") {
            return <p><span className="bold">Coming Soon</span></p>;
          };
    
        const matchingHour = store.hours.find(hour => {
          const startIndex = days.indexOf(hour.start);
          const endIndex = days.indexOf(hour.end);
          return day >= startIndex && day <= endIndex;
        });
    
        const matchingOverride = Object.keys(store.hours_override)
          .filter(date => date == dateOverride)
          .map(date => store.hours_override[date]);
        
        const matchingClosedDays =  store.closed_days.find(closedDay => {
          const capitalizeClosedDay = closedDay.charAt(0).toUpperCase() + closedDay.slice(1);
          return capitalizeClosedDay.includes(days[day]);
        });
    
        if (matchingOverride.length > 0) {
            if (matchingOverride[0].closed_all_day == "1") {
              return <p><span className="bold">CLOSED</span></p>;
            } else {
              return <p><span className="bold">OPEN</span> <span className="no-translate">{convertTimeFormat(matchingOverride[0].open_time)} - {convertTimeFormat(matchingOverride[0].close_time)}</span></p> ;
            }
        };
      
        if (matchingHour) {
        return <p><span className="bold">OPEN</span> <span className="no-translate">{matchingHour.open} - {matchingHour.close}</span></p>
        };
    
        if (matchingClosedDays) {
          return <p><span className="bold">CLOSED</span></p>;
        };
    
    };

    const handleMarkerClick = (store, key) => { // send data to store lise on marker click   
        const hours = getHoursToday(store);
        const address1 = store.address.address_1;
        const address2 = store.address.city + " " + store.address.state + " " + store.address.postal_code;
        locationClickEvent(parseFloat(store.latitude), parseFloat(store.longitude));
        setInfoBoxPosition({ lat: store.latitude, lng: store.longitude, name: store.name, address1: address1, address2: address2, hours: hours });

        setTimeout(() => {
            setLocationClick();
        }, "1000");
    };

    const handleMarkerClusterClick = (cluster) => {
        setTimeout(() => {
            setClientLatitude(cluster.center.lat());
            setClientLongitude(cluster.center.lng());
        }, "1500");
    };

    const renderMarkers = (store, key, clusterer) => {  // renders markers from store data
        const latitudeToNumber = parseFloat(store.latitude);
        const longitudeToNumber = parseFloat(store.longitude);
        const markerPosition = {
            lat: parseFloat(latitudeToNumber.toFixed(6)),
            lng: parseFloat(longitudeToNumber.toFixed(6)),
        };

        const icon = "/wp-content/themes/gfsstore.com/images/Map-Point-Icon11.png";

        return (<Marker position={markerPosition} onClick={() => handleMarkerClick(store, key)} clusterer={clusterer} icon={icon} />);
    };

    const renderInfobox = () => {
        if(infoBoxPosition.name) {
            var slashInName = infoBoxPosition.name.includes('/ ') ? infoBoxPosition.name : '';
            var beforeSlash = infoBoxPosition.name.substring(0, slashInName.indexOf("/"));
            var afterSlash = infoBoxPosition.name.substring(slashInName.indexOf("/") + 2, slashInName.length);
            if (slashInName.length > 13) {
                slashInName = <h2 class="no-translate">{beforeSlash} /<br/>{afterSlash}</h2>
            } else if (slashInName > 1) {
                slashInName = <h2 class="no-translate">{beforeSlash} / {afterSlash}</h2>
            } else {
                slashInName = <h2 class="no-translate">{infoBoxPosition.name}</h2>
            }
        }

        const googleDirections = infoBoxPosition.address1 + " " +  infoBoxPosition.address2;
        const addressParts = googleDirections.split(' ');
        const formattedAddress = addressParts.map(part => encodeURIComponent(part)).join('+');

        return (
            infoBoxPosition.lat ? (
                <InfoBox
                    options={{ closeBoxURL: '', enableEventPropagation: true }}
                    position={{ lat: infoBoxPosition.lat, lng: infoBoxPosition.lng }}
                >
                    <div>
                        <div className="arrow-pointer"></div>
                        <div className="infobox-container">
                            <div className="infobox-text-container">
                                <button className="infobox-close-btn" onClick={() => closeInfoBox()} aria-label="Close Infobox"><i class="fa-solid fa-xmark"></i></button>
                                {slashInName}
                                <a href={`https://www.google.com/maps/dir/?api=1&destination=Gordon+Food+Service+Store+${formattedAddress}`} className="info-box-directions no-translate" target="_blank" rel="noopener noreferrer">
                                    <p>{infoBoxPosition.address1}</p>
                                    <p>{infoBoxPosition.address2}</p>
                                </a>
                                {infoBoxPosition.hours}
                            </div>
                        </div>
                    </div>
                </InfoBox>
            ) : null
        );
    };

    const closeInfoBox = () => {
        setInfoBoxPosition({ lat: "", lng: "", name: "", address1: "", address2: "", hours: "" });
    };

    const renderMap = () => {  // renders Map if store data and location are set 
        const markerClusterOptions = {
            imagePath: "/wp-content/themes/gfsstore.com/images/Map-Point-Icon",
            imageExtension: "png",
            gridSize: 50,
            minimumClusterSize: 2
        };

        if (clientLatitude !== 0 && clientLongitude !== 0 && storeData && currentZoom) {
            return (<section className="map-container" >
                <GoogleMap
                    ref={map => { setMapDetails(map); }}
                    mapContainerStyle={{ height: '400px', width: '100%' }}
                    center={{ lat: clientLatitude, lng: clientLongitude }}
                    zoom={currentZoom}
                    onCenterChanged={() => { mapCenterChanged() }}
                    onZoomChanged={() => { mapCenterChanged() }}
                >
                    {renderInfobox()}

                    <MarkerClusterer
                        ref={cluster => { setClusterDetails(cluster); }}
                        options={markerClusterOptions}
                        onClick={(cluster) => { handleMarkerClusterClick(cluster) }}>
                        {(clusterer) =>
                        (<div className="cluster-div">
                            {Object.entries(storeData).map(([key, value]) => (
                                renderMarkers(value, key, clusterer)
                            ))}
                        </div>)
                        }
                    </MarkerClusterer>
                </GoogleMap>
            </section>)
        } else {
            return (
                <section>
                    <p>No Location Found</p>
                </section>
            );
        };
    };

    const renderStoreList = () => {  // renders store list side bar 
        if (clientLatitude !== 0 && clientLongitude !== 0 && storeData && mapCenter) {
            return (
                <section className="store-list">
                    <div>
                        <StoreList stores={currentStoreData} clientLatitude={clientLatitude} clientLongitude={clientLongitude} currentLocation={mapCenter} currentZoom={currentZoom} />
                    </div>
                </section>
            );
        };
    };

    return (
        <div className="store-locator-content">
            {renderStoreList()}
            {renderMap()}
        </div>
    );
};

export default React.memo(MapClass);